// Typed bridge to globals provided by the re-mounted legacy dialog stack
// (see app/views/encounter/_monitor_react_extra_body.html.erb). These exist
// only when the React monitor view is active, so all accessors are best-effort.

interface MonitorWindowGlobals {
  monitorReactSelectedIds?: number[];
  monitorReactReload?: () => void;
  monitorReactPatchLocks?: (data: unknown) => void;
  monitorReactSetPatientDetails?: (html: string) => void;
  monitorReactReloadDetails?: () => void;
  signSelectedEncounters?: () => void;
  signAllStableEncounters?: () => void;
  unlockSelectedPatients?: () => void;
  releaseLocks?: () => void;
  initCurrentTab?: () => void;
  enablePrint?: (fn: () => void) => void;
  setCurrentPatient?: (id: number | null, info: unknown) => void;
  setNavigationMenu?: (menuId: string) => void;
  idler?: () => { addChildWindow: (win: Window | null) => void };
  startProgress?: (message: string) => void;
  startProgressSoft?: (message: string) => void;
  stopProgress?: (message?: string) => void;
  treatment_teamChanged?: (value: string) => void;
  care_managerChanged?: () => void;
}

const monitorWindow = (): MonitorWindowGlobals => window as unknown as MonitorWindowGlobals;

export function publishSelectedIds(ids: number[]): void {
  monitorWindow().monitorReactSelectedIds = ids;
}

export function publishReloadCallback(fn: () => void): void {
  monitorWindow().monitorReactReload = fn;
}

export function signSelectedEncounters(): void {
  monitorWindow().signSelectedEncounters?.();
}

export function signAllStableEncounters(): void {
  monitorWindow().signAllStableEncounters?.();
}

export function unlockSelectedPatients(): void {
  monitorWindow().unlockSelectedPatients?.();
}

export function releaseLocks(): void {
  monitorWindow().releaseLocks?.();
}

export function initCurrentTab(): void {
  monitorWindow().initCurrentTab?.();
}

export function enablePrint(fn: () => void): void {
  monitorWindow().enablePrint?.(fn);
}

export function setCurrentPatient(id: number | null, info: unknown): void {
  monitorWindow().setCurrentPatient?.(id, info);
}

export function setNavigationMenu(menuId: string): void {
  monitorWindow().setNavigationMenu?.(menuId);
}

export function addChildWindow(win: Window | null): void {
  const fn = monitorWindow().idler;
  if (typeof fn === "function") {
    fn().addChildWindow(win);
  }
}

export function startProgress(message: string): void {
  monitorWindow().startProgress?.(message);
}

export function stopProgress(message?: string): void {
  monitorWindow().stopProgress?.(message);
}

export function publishLockPatcher(fn: (data: unknown) => void): void {
  monitorWindow().monitorReactPatchLocks = fn;
}

// PatientDetailsPanel publishes a React state setter here so the re-mounted legacy
// dialog stack can hand refreshed current_readings HTML back to React (via opComplete
// and the invalidate/revalidate dialogs) instead of $j("#patient-details").html(...).
// #patient-details is React-owned; a direct jQuery .html() destroys React's child
// nodes and crashes the next render with a removeChild NotFoundError (HHMCOVID-6234).
export function publishDetailsHtmlSetter(fn: (html: string) => void): void {
  monitorWindow().monitorReactSetPatientDetails = fn;
}

// Lets legacy dialogs that don't return current_readings HTML (clear-hold, clear-expired,
// override-lock) refresh the open details panel by re-fetching it through PatientDetailsPanel's
// normal (patient-keyed) fetch, so its hold/lock banner doesn't stay stale (HHMCOVID-6234).
export function publishDetailsReloader(fn: () => void): void {
  monitorWindow().monitorReactReloadDetails = fn;
}

export function publishFilterChangeHandlers(handlers: {
  onTreatmentTeamChange?: (value: string) => void;
  onCareManagerChange?: () => void;
}): void {
  if (handlers.onTreatmentTeamChange) {
    monitorWindow().treatment_teamChanged = handlers.onTreatmentTeamChange;
  }
  if (handlers.onCareManagerChange) {
    monitorWindow().care_managerChanged = handlers.onCareManagerChange;
  }
}
